import { Box, Button, Collapse, FormLabel } from '@chakra-ui/react';
import { Form, Formik } from 'formik';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { FormikSelect, FormikSwitch } from 'components/Formik';
import LogoLoader from 'components/Loader/LogoLoader';
import Title from 'components/Title';
import FEATURES from 'global/features';
import { routePaths } from 'global/routePaths';
import { useFetchSettings } from 'hooks/companySettings/useFetchSettings';
import { useSaveScheduleSettings, ISaveScheduleSettings } from 'hooks/companySettings/useSaveScheduleSettings';
import { useFeatureList } from 'store/useFeatureStore';

import { initialScheduleData } from './schema';
import { scheduleMinuteOptions } from '../shared/options';

const Schedule: React.FC<unknown> = () => {
  const { t } = useTranslation(['common', 'companySettings']);

  const [formData, setFormData] = useState<any>(initialScheduleData);

  const { data, status, refetch } = useFetchSettings('scheduling');
  const featureList: any = useFeatureList();
  const { status: saveScheduleStatus, mutateAsync } = useSaveScheduleSettings();
  const navigate = useNavigate();

  useEffect(() => {
    if (!featureList[FEATURES.scheduling]) {
      navigate(routePaths.companySettings.base.route);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (status === 'success') {
      const fetchedFormData = data?.payload;

      const formData: any = {
        manage_schedule_shift: fetchedFormData.manage_schedule_shift,
        allow_schedule_overlapping: fetchedFormData.allow_schedule_overlapping,
        show_employees_sched_assigned_users: fetchedFormData.show_employees_sched_assigned_users,
        requires_schedule_to_clock_in: fetchedFormData.requires_schedule_to_clock_in,
        requires_schedule_to_clock_within_period: fetchedFormData.requires_schedule_to_clock_within_period
          ? scheduleMinuteOptions.find(
              ({ value }) => value === fetchedFormData.requires_schedule_to_clock_within_period
            )
          : { label: '0 min', value: 0 },
      };
      setFormData(formData);
    }
  }, [status, data?.payload]);

  const handleToggle = (
    e: any,
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void,
    controlName: string
  ) => {
    setFieldValue(controlName, e.target.checked);
  };

  const handleFormSubmit = async (values: typeof initialScheduleData) => {
    const postData: ISaveScheduleSettings = {
      manage_schedule_shift: values.manage_schedule_shift,
      allow_schedule_overlapping: values.allow_schedule_overlapping,
      show_employees_sched_assigned_users: values.show_employees_sched_assigned_users,
      requires_schedule_to_clock_in: values.requires_schedule_to_clock_in,
      requires_schedule_to_clock_within_period: values.requires_schedule_to_clock_in
        ? values.requires_schedule_to_clock_within_period?.value || 0
        : 0,
    };
    mutateAsync(postData).then(() => {
      refetch();
    });
  };

  if (status === 'loading') return <LogoLoader />;

  return (
    <Box>
      <Box mb={{ base: '2', md: '0' }}>
        <Title hideBackButton={true}> {t('companySettings:scheduleSetttings')}</Title>
      </Box>
      <Box mt="26px">
        <Formik initialValues={formData} enableReinitialize onSubmit={handleFormSubmit}>
          {({ setFieldValue, values }) => (
            <Form noValidate>
              <Box display="flex" flexDirection="column" maxWidth="390px">
                <Box display="flex" justifyContent="space-between" mb="17px">
                  <FormLabel fontSize="sm">{t('companySettings:allowUsersToAcceptOrDeclineScheduledShift')}</FormLabel>
                  <FormikSwitch
                    name="manage_schedule_shift"
                    onChangeCallback={e => handleToggle(e, setFieldValue, 'manage_schedule_shift')}
                  ></FormikSwitch>
                </Box>
                <Box display="flex" justifyContent="space-between" mb="17px">
                  <FormLabel fontSize="sm">{t('companySettings:allowUserToHaveOverlappingSchedules')}</FormLabel>
                  <FormikSwitch
                    name="allow_schedule_overlapping"
                    onChangeCallback={e => handleToggle(e, setFieldValue, 'allow_schedule_overlapping')}
                  ></FormikSwitch>
                </Box>
                <Box display="flex" justifyContent="space-between" mb="17px">
                  <FormLabel fontSize="sm">{t('companySettings:showOnMobileAppWhoElseIsAssignedToShift')}</FormLabel>
                  <FormikSwitch
                    name="show_employees_sched_assigned_users"
                    onChangeCallback={e => handleToggle(e, setFieldValue, 'show_employees_sched_assigned_users')}
                  ></FormikSwitch>
                </Box>
                <Box display="flex" justifyContent="space-between" mb="17px">
                  <FormLabel fontSize="sm">{t('companySettings:userCanOnlyClockInToAScheduleShift')}</FormLabel>
                  <FormikSwitch
                    name="requires_schedule_to_clock_in"
                    onChangeCallback={e => handleToggle(e, setFieldValue, 'requires_schedule_to_clock_in')}
                  ></FormikSwitch>
                </Box>

                <Collapse in={values.requires_schedule_to_clock_in} unmountOnExit={true} animateOpacity>
                  <Box maxWidth="390px">
                    <FormikSelect
                      name="requires_schedule_to_clock_within_period"
                      label={t('companySettings:numberOfMinutesBeforeClockingIntoAShift')}
                      options={scheduleMinuteOptions}
                      selectProps={{
                        placeholder: t('companySettings:selectMinutes'),
                        isClearable: false,
                      }}
                    />
                  </Box>
                </Collapse>
              </Box>
              <Button
                type="submit"
                colorScheme="secondary"
                isLoading={saveScheduleStatus === 'loading'}
                minWidth="100px"
              >
                {t('common:save')}
              </Button>
            </Form>
          )}
        </Formik>
      </Box>
    </Box>
  );
};
export default Schedule;
